import axios from 'axios';
import { IBoard, IBoardMember, ICreateBoardRequest, IUpdateBoardRequest, IBoardFilters, IPaginatedResponse } from '../types';

const API_URL = `${process.env.REACT_APP_API_URL}/workflow`;

/**
 * Board API Servisleri
 */
export const boardsApi = {
  /**
   * Board listesini getir
   */
  list: async (filters?: IBoardFilters): Promise<IPaginatedResponse<IBoard>> => {
    const response = await axios.get<IPaginatedResponse<IBoard>>(`${API_URL}/boards`, {
      params: filters,
    });
    return response.data;
  },

  /**
   * Tek bir board getir (steps ve members ile)
   */
  get: async (boardId: number): Promise<IBoard> => {
    const response = await axios.get<IBoard>(`${API_URL}/boards/${boardId}`);
    return response.data;
  },

  /**
   * Yeni board oluştur
   */
  create: async (data: ICreateBoardRequest): Promise<IBoard> => {
    const response = await axios.post<IBoard>(`${API_URL}/boards`, data);
    return response.data;
  },

  /**
   * Board güncelle
   */
  update: async (boardId: number, data: IUpdateBoardRequest): Promise<IBoard> => {
    const response = await axios.patch<IBoard>(`${API_URL}/boards/${boardId}`, data);
    return response.data;
  },

  /**
   * Board sil (arşivle)
   */
  delete: async (boardId: number): Promise<{ message: string }> => {
    const response = await axios.delete<{ message: string }>(`${API_URL}/boards/${boardId}`);
    return response.data;
  },

  /**
   * Board üyelerini listele
   */
  getMembers: async (boardId: number): Promise<IBoardMember[]> => {
    const response = await axios.get<IBoardMember[]>(`${API_URL}/boards/${boardId}/members`);
    return response.data;
  },

  /**
   * Üye ekle
   */
  addMember: async (boardId: number, userId: number, role: 'admin' | 'editor' | 'viewer' | 'member' = 'member'): Promise<IBoardMember> => {
    const response = await axios.post<IBoardMember>(`${API_URL}/boards/${boardId}/members`, {
      user_id: userId,
      role,
    });
    return response.data;
  },

  /**
   * Üye rolünü güncelle
   */
  updateMember: async (boardId: number, userId: number, role: 'admin' | 'editor' | 'viewer' | 'member'): Promise<IBoardMember> => {
    const response = await axios.patch<IBoardMember>(`${API_URL}/boards/${boardId}/members/${userId}`, { role });
    return response.data;
  },

  /**
   * Üye rolünü güncelle (alias)
   */
  updateMemberRole: async (boardId: number, userId: number, role: 'admin' | 'editor' | 'viewer' | 'member'): Promise<IBoardMember> => {
    const response = await axios.patch<IBoardMember>(`${API_URL}/boards/${boardId}/members/${userId}`, { role });
    return response.data;
  },

  /**
   * Üye çıkar
   */
  removeMember: async (boardId: number, userId: number): Promise<{ message: string }> => {
    const response = await axios.delete<{ message: string }>(`${API_URL}/boards/${boardId}/members/${userId}`);
    return response.data;
  },

  /**
   * Şirket kullanıcılarını ara
   */
  searchUsers: async (query: string): Promise<{ results: Array<{ id: number; name: string; email: string; avatar?: string }> }> => {
    const response = await axios.get(`${API_URL}/boards/users/search`, {
      params: { q: query },
    });
    return response.data;
  },

  /**
   * Dashboard özeti getir (bekleyen görevler ve panolar)
   */
  getDashboardSummary: async (): Promise<IDashboardSummary> => {
    const response = await axios.get<IDashboardSummary>(`${API_URL}/boards/dashboard-summary`);
    return response.data;
  },
};

/**
 * Dashboard özeti için tip tanımları
 */
export interface IDashboardSummaryBoard {
  id: number;
  name: string;
  color: string;
  role: 'admin' | 'editor' | 'viewer' | 'member';
  task_count: number;
}

export interface IDashboardSummary {
  pending_task_count: number;
  boards: IDashboardSummaryBoard[];
  total_boards: number;
}

export default boardsApi;
